import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Alert, Divider, Grid, Paper, Snackbar } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CryptoJS from "crypto-js";

import { useGetPartnerBU } from "../../../hooks/useGetPartnerBU";
import { useStudioEad } from "../../../hooks/useStudioEad";
import { useYt } from "../../../hooks/useYt";
import { AeskinsSkeletons } from "./components/Skeletons/Aeskins";
import { TrainingButtons } from "./components/TrainingButtons";
import { VideoList } from "./components/VideoList";
import { VideoPlayer } from "./components/VideoPlayer";

export default function Treinamentos() {
  const playerRef = useRef(null);

  const {
    handleRedirectToMedsystemsAcademy,
    handleRedirectToBeautyAcademy,
    handleGetUserInfo,
    handleGetCourses,
  } = useStudioEad();
  const { handleAeskinsVideos } = useYt();

  const [volume, setVolume] = useState(50);
  const [videos, setVideos] = useState([]);
  const [currentVideo, setCurrentVideo] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState("");
  const [open, setOpen] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [loadingStudioEad, setLoadingStudioEad] = useState();
  const [userCourses, setUserCourses] = useState(null);

  const { errorPartnerBU, loadingGetBU, partnerBU } = useGetPartnerBU();

  const userInfos = useMemo(() => {
    const password = "MedSys@341";
    const cnpjDecrypt = CryptoJS.AES.decrypt(
      localStorage.getItem("user"),
      password,
    );

    const customerNameDecrypt = CryptoJS.AES.decrypt(
      localStorage.getItem("nomeparc"),
      password,
    );
    const customerName = CryptoJS.enc.Utf8.stringify(customerNameDecrypt);
    const cnpj = CryptoJS.enc.Utf8.stringify(cnpjDecrypt);

    return {
      cnpj,
      customerName: customerName?.trim?.(),
    };
  }, []);

  const handleClick = () => {
    setOpen(true);
  };

  const handleCloseToast = (reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenToast(false);
  };

  const handleChangeAccordion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleFullscreen = () => {
    if (playerRef.current) {
      playerRef.current.wrapper.requestFullscreen();
    }
  };

  const handleClose = (reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const handleChangeVideos = ({
    title,
    description,
    id,
    course,
    topico,
    position,
  }) => {
    setCurrentVideo({
      title,
      description,
      id,
      course,
      topico,
      position,
    });
  };

  const handleGetVideos = useCallback(async () => {
    try {
      setLoading(true);

      const videosData = await handleAeskinsVideos();
      console.log("Videos data fetched and processed:", videosData);
      setVideos(videosData);

      const firstTopicName = Object.keys(videosData)[0];
      const firstCourseName = Object.keys(videosData[firstTopicName])[0];
      const firstVideo = videosData[firstTopicName][firstCourseName][0];

      setCurrentVideo({
        title: firstVideo?.title,
        description: firstVideo?.description,
        id: firstVideo?.id,
        course: firstCourseName,
        topico: firstTopicName,
        position: 0,
      });

      setExpanded(firstCourseName);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [handleAeskinsVideos]);

  const handleNextVideo = () => {
    const topico = currentVideo?.topico;
    const curso = currentVideo?.course;
    const pos = currentVideo?.position;
    const nextVideo = videos?.[topico]?.[curso]?.[pos + 1];

    if (nextVideo) {
      handleClick();
      handleChangeVideos({
        title: nextVideo?.title,
        description: nextVideo?.description,
        id: nextVideo?.id,
        course: curso,
        topico,
        position: pos + 1,
      });
    }
  };

  const handleStudioEad = async (type) => {
    try {
      setLoadingStudioEad(type);
      setOpenToast(false);

      const userData = await handleGetUserInfo({
        jsonid: localStorage.getItem("token"),
        cnpj: userInfos?.cnpj,
      });

      if (type === "med") {
        const data = await handleRedirectToMedsystemsAcademy({
          course: userCourses?.medsystemsAcademy,
          email: userData?.email,
          customerName: userInfos?.customerName,
        });

        if (data?.url) {
          window.location.href = data.url;
        }
        return;
      }

      const data = await handleRedirectToBeautyAcademy({
        course: userCourses?.beautyAcademy,
        email: userData?.email,
        customerName: userInfos?.customerName,
      });

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error(error);
      setOpenToast(true);
    } finally {
      setLoadingStudioEad(null);
    }
  };

  const handleGetUserCourses = useCallback(async () => {
    try {
      const courses = await handleGetCourses({
        cnpj: userInfos?.cnpj,
      });

      setUserCourses(courses);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingStudioEad();
    }
  }, [handleGetCourses, userInfos?.cnpj]);

  const verifyBU = useMemo(() => {
    const isMedicalBU = partnerBU?.data?.some((bu) => bu.CODCENCUS === 3010100);
    const isBeautyBU = partnerBU?.data?.some((bu) => bu.CODCENCUS === 3010500);
    return { isMedicalBU, isBeautyBU };
  }, [partnerBU]);

  useEffect(() => {
    handleGetVideos();
  }, []);

  useEffect(() => {
    handleGetUserCourses();
  }, []);

  if (loading) {
    return <AeskinsSkeletons />;
  }

  if (!videos && !loading) {
    return (
      <Typography variant="h1" fontSize={25}>
        Não foi possível carregar os vídeos
      </Typography>
    );
  }

  return (
    <Box>
      <Snackbar
        open={openToast}
        autoHideDuration={10000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Não foi possível redirecionar para o Studio EAD. Por favor, tente
          novamente.
        </Alert>
      </Snackbar>

      <Grid container>
        <Grid item xs={12} md={12}>
          {(userCourses?.medsystemsAcademy || userCourses?.beautyAcademy) && (
            <>
              <Grid
                container
                alignItems="center"
                justifyContent="flex-start"
                width={{ xs: "100%", md: "100%" }}
                mb={4}
              >
                <Grid item xs={12} md={3.2}>
                  <Typography variant="h1" fontSize={25} fontWeight={500}>
                    Agende aqui o seu treinamento
                    <br />
                    oficial da tecnologia:
                  </Typography>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={8}
                  display="flex"
                  justifyContent={{ xs: "flex-start", md: "flex-start" }}
                  gap={1}
                  mt={{ xs: 2, md: 0 }}
                >
                  <TrainingButtons
                    handleStudioEad={handleStudioEad}
                    loadingStudioEad={loadingStudioEad}
                    userCourses={userCourses}
                    verifyBU={verifyBU}
                    loadingGetBU={loadingGetBU}
                    errorPartnerBU={errorPartnerBU}
                  />
                </Grid>
              </Grid>

              <Divider
                sx={{
                  width: "100%",
                  mb: "11.5px",
                  borderColor: "#707070",
                }}
              />
            </>
          )}
        </Grid>

        <Grid container gap={3.5}>
          <Grid item xs={12} md={3}>
            <Box
              maxHeight={650}
              borderRadius="10px"
              sx={{ maxHeight: 650, overflowY: "auto" }}
            >
              {Object.entries(videos).map(([topico, cursos]) => (
                <Paper
                  key={topico}
                  sx={{ mb: 2, borderRadius: "10px", border: "1px solid #eee" }}
                >
                  <Typography variant="subtitle1" padding={2}>
                    {topico}
                  </Typography>

                  {Object.entries(cursos).map(([curso, conteudos]) => (
                    <VideoList
                      key={curso}
                      entries={conteudos}
                      courseName={curso}
                      topicName={topico}
                      expanded={expanded}
                      handleChangeAccordion={handleChangeAccordion}
                      currentVideo={currentVideo}
                      handleChangeVideos={handleChangeVideos}
                    />
                  ))}
                </Paper>
              ))}
            </Box>
          </Grid>

          <Grid item xs={6} md={7}>
            {Object?.entries(videos)?.length === 0 ? (
              <Alert severity="info" variant="filled" sx={{ width: "100%" }}>
                Nenhum vídeo da Medsystems encontrado
              </Alert>
            ) : (
              <VideoPlayer
                playerRef={playerRef}
                url={currentVideo?.id}
                volume={volume}
                setVolume={setVolume}
                onEnded={handleNextVideo}
                handleFullscreen={handleFullscreen}
                currentVideo={currentVideo}
              />
            )}
          </Grid>
        </Grid>
      </Grid>

      <Snackbar
        open={open}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="info" sx={{ width: "100%" }}>
          O vídeo terminou. O próximo vídeo será reproduzido em instantes!
        </Alert>
      </Snackbar>
    </Box>
  );
}
